<template>
  <div class="quadstar-plan-selector">
    <div class="selection-header">
      <div class="d-flex justify-content-between align-items-center">
        <div class="d-flex align-items-center">
          <h3 class="mb-0">Create Your Bundle</h3>
          <span class="badge bg-primary ms-2" v-if="isEligibleForDiscount()">10% OFF</span>
        </div>
        <div class="contract-toggle">
          <div class="btn-group contract-toggle-btn" role="group">
            <button 
              type="button" 
              class="btn" 
              :class="{'btn-primary': !isLongContract, 'btn-outline-primary': isLongContract}"
              @click="isLongContract = false"
            >
              12 Months
            </button>
            <button 
              type="button" 
              class="btn" 
              :class="{'btn-primary': isLongContract, 'btn-outline-primary': !isLongContract}"
              @click="isLongContract = true"
            >
              24 Months
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="categories-and-summary">
      <!-- Tabs Navigation -->
      <div class="categories-tabs">
        <div 
          v-for="(category, index) in categories" 
          :key="category.type"
          class="category-tab" 
          :class="{ 'active': activeCategory === category.type }"
          @click="activeCategory = category.type"
        >
          <div class="category-icon">
            <i :class="'bi ' + category.icon"></i>
          </div>
          <div class="category-name">
            {{ category.label }}
            <span v-if="selectedPlans[category.type]" class="category-selected">
              <i class="bi bi-check-circle-fill"></i>
            </span>
          </div>
          <div class="category-toggle">
            <div class="form-check form-switch">
              <input 
                class="form-check-input" 
                type="checkbox" 
                :id="'toggle-' + category.type"
                :checked="selectedPlans[category.type] !== null"
                @change="toggleCategory(category.type, $event)"
              >
            </div>
          </div>
        </div>
      </div>
      
      <!-- Plans Display -->
      <div class="plans-display">
        <div v-if="selectedPlans[activeCategory] === null" class="no-plan-selected">
          <div class="text-center py-4">
            <i class="bi bi-toggle-off no-plan-icon"></i>
            <p>This service is currently disabled</p>
            <button class="btn btn-sm btn-outline-primary" @click="enableCategory(activeCategory)">
              Enable {{ getCategoryLabel(activeCategory) }}
            </button>
          </div>
        </div>
        
        <div v-else class="plan-cards-container">
          <div 
            v-for="plan in getCategoryPlans(activeCategory)" 
            :key="plan.name"
            class="plan-card"
            :class="{ 'selected': selectedPlans[activeCategory] === plan }"
            @click="selectPlan(activeCategory, plan)"
          >
            <div class="plan-visual">
              <i :class="getCategoryIcon(activeCategory)" class="plan-icon"></i>
            </div>
            <div class="plan-details">
              <div class="plan-card-header">{{ plan.name }}</div>
              <div class="plan-card-price">€{{ isLongContract ? plan.price24 : plan.price12 }}</div>
              <div class="plan-card-description">{{ getPlanDescription(plan) }}</div>
              <div class="plan-card-features" v-if="plan.features && plan.features.length">
                <span class="feature-pill" v-for="feature in plan.features" :key="feature">{{ feature }}</span>
              </div>
              <div class="plan-card-features" v-else>
                <span class="feature-pill" v-if="plan.dataAllowance">{{ plan.dataAllowance }}</span>
                <span class="feature-pill" v-if="plan.callingAllowance">{{ plan.callingAllowance }} calls</span>
                <span class="feature-pill" v-if="plan.smsAllowance">{{ plan.smsAllowance }} SMS</span>
              </div>
            </div>
          </div>
          
          <!-- Installation fee notice for Internet -->
          <div v-if="activeCategory === 'internet'" class="installation-notice">
            <i class="bi bi-info-circle"></i>
            Internet installation fee: €85 for 12-month contracts. Free with 24-month contracts.
          </div>
          
          <!-- Installation fee notice for TV -->
          <div v-if="activeCategory === 'tv'" class="installation-notice">
            <i class="bi bi-info-circle"></i>
            TV installation fee: €55 for 12-month contracts. Free with 24-month contracts.
          </div>
          
          <!-- Overage rates for Mobile -->
          <div v-if="activeCategory === 'mobile'" class="overage-rates">
            <div class="overage-title">Overage rates</div>
            <div class="overage-detail">Out of bundle data usage: €80 per additional GB / €0.08 per MB.</div>
            <div class="overage-detail">Out of bundle calling minutes: €0.20 per minute</div>
            <div class="overage-detail">Out of bundle SMS: €0.10 per message</div>
          </div>
        </div>
      </div>
      
      <!-- Summary Section -->
      <div class="summary-panel">
        <h3>Package Summary</h3>
        <div class="summary-items">
          <div v-for="(plan, type) in getSelectedPlans()" :key="type" class="summary-item">
            <div class="d-flex align-items-center">
              <i :class="getCategoryIcon(type)" class="summary-icon"></i>
              <span class="summary-label">{{ plan ? plan.name : 'No ' + getCategoryLabel(type) }}</span>
            </div>
            <div class="summary-price">{{ plan ? '€' + (isLongContract ? plan.price24 : plan.price12) : '€0' }}</div>
          </div>
        </div>
        <div class="summary-total">
          <div class="total-label">Subtotal</div>
          <div class="total-value">€{{ calculateSubtotal() }}</div>
        </div>
        <div v-if="isEligibleForDiscount()" class="discount-row">
          <div class="discount-label">Bundle Discount (10%)</div>
          <div class="discount-value">-€{{ calculateDiscount() }}</div>
        </div>
        <div v-if="parseFloat(calculateInstallFees()) > 0" class="install-fee-row">
          <div class="fee-label">Installation Fee</div>
          <div class="fee-value">€{{ calculateInstallFees() }}</div>
        </div>
        <div class="contract-note" v-if="!isLongContract && (selectedPlans.tv || selectedPlans.internet)">
          <i class="bi bi-info-circle-fill"></i>
          No installation fees with 24-month contract!
        </div>
        <div class="summary-total grand-total">
          <div class="total-label">Total</div>
          <div class="total-value">€{{ (parseFloat(calculateTotal()) + parseFloat(calculateInstallFees())).toFixed(2) }}</div>
        </div>
        <button @click="submitOrder" class="btn btn-primary w-100 mt-2 subscribe-btn">
          <i class="bi bi-check-circle-fill me-2"></i>Subscribe Now
        </button>
        <div class="mt-2 text-center">
          <button @click="resetPlans" class="btn btn-link btn-sm text-muted reset-btn">
            <i class="bi bi-arrow-counterclockwise"></i> Reset Selections
          </button>
        </div>
      </div>
      
      <!-- Order Summary Modal -->
      <div v-if="showOrderSummary" class="order-summary-modal">
        <div class="order-summary-content">
          <div class="order-summary-header">
            <h2>Order Summary</h2>
            <button @click="closeOrderSummary" class="close-btn">
              <i class="bi bi-x"></i>
            </button>
          </div>
          <div class="order-summary-body">
            <div class="order-section">
              <h3>Selected Plans</h3>
              <div v-for="(plan, type) in getSelectedPlans()" :key="type" class="order-plan-item">
                <div v-if="plan" class="plan-item-details">
                  <div class="plan-item-icon">
                    <i :class="getCategoryIcon(type)"></i>
                  </div>
                  <div class="plan-item-info">
                    <div class="plan-item-name">{{ plan.name }}</div>
                    <div class="plan-item-desc">{{ getPlanDescription(plan) }}</div>
                  </div>
                  <div class="plan-item-price">€{{ isLongContract ? plan.price24 : plan.price12 }}/mo</div>
                </div>
              </div>
            </div>
            
            <div class="order-section">
              <h3>Pricing Details</h3>
              <div class="order-detail-item">
                <span>Contract Length:</span>
                <span>{{ isLongContract ? '24 months' : '12 months' }}</span>
              </div>
              <div class="order-detail-item">
                <span>Monthly Subtotal:</span>
                <span>€{{ calculateSubtotal() }}</span>
              </div>
              <div v-if="isEligibleForDiscount()" class="order-detail-item discount">
                <span>Bundle Discount (10%):</span>
                <span>-€{{ calculateDiscount() }}</span>
              </div>
              <div class="order-detail-item total">
                <span>Monthly Total:</span>
                <span>€{{ calculateTotal() }}</span>
              </div>
              
              <div v-if="parseFloat(calculateInstallFees()) > 0" class="order-detail-item fee">
                <span>One-time Installation Fee:</span>
                <span>€{{ calculateInstallFees() }}</span>
              </div>
            </div>
            
            <div class="order-section">
              <h3>Contract Terms</h3>
              <p>By proceeding with this order, you agree to a {{ isLongContract ? '24' : '12' }}-month contract with automatic renewal. Early termination fees may apply.</p>
              <p>Your first bill will include the monthly charge of €{{ calculateTotal() }} plus any applicable installation fees (€{{ calculateInstallFees() }}).</p>
            </div>
            
            <div class="order-actions">
              <button @click="closeOrderSummary" class="btn btn-outline-secondary">
                <i class="bi bi-arrow-left"></i> Modify Selection
              </button>
              <button class="btn btn-primary">
                <i class="bi bi-check-lg"></i> Confirm Order
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, watch } from 'vue'

export default {
  name: 'QuadstarPlanSelector',
  setup() {
    // Helper for localStorage persistence
    const getStoredValue = (key, defaultValue) => {
      try {
        const storedValue = localStorage.getItem(`planSelector.${key}`);
        if (storedValue !== null) {
          return JSON.parse(storedValue);
        }
      } catch (e) {
        console.error('Error reading from localStorage:', e);
      }
      return defaultValue;
    };

    const storeValue = (key, value) => {
      try {
        localStorage.setItem(`planSelector.${key}`, JSON.stringify(value));
      } catch (e) {
        console.error('Error writing to localStorage:', e);
      }
    };
    
    // Toggle between 12-month and 24-month pricing
    const isLongContract = ref(getStoredValue('isLongContract', true))
    
    // Active category tab
    const activeCategory = ref(getStoredValue('activeCategory', 'mobile'))
    
    // Category definitions
    const categories = [
      { 
        type: 'mobile', 
        label: 'Mobile',
        icon: 'bi-phone'
      },
      { 
        type: 'tv', 
        label: 'TV',
        icon: 'bi-tv' 
      },
      { 
        type: 'internet', 
        label: 'Internet',
        icon: 'bi-wifi' 
      },
      { 
        type: 'fixed', 
        label: 'Fixed',
        icon: 'bi-telephone' 
      }
    ]

    // Track plan IDs for storage
    const plansByName = {
      mobile: {},
      tv: {},
      internet: {},
      fixed: {}
    };
    
    // Get stored plan selections or null
    const storedSelections = getStoredValue('selectedPlans', {
      mobile: 'Mobile Flex',
      tv: 'TV Premium',
      internet: 'Fiber Connect 100MBit',
      fixed: null
    });
    
    // Selected plans - will be populated with actual plan objects in onMounted
    const selectedPlans = reactive({
      mobile: null, 
      tv: null,    
      internet: null,
      fixed: null
    })

    // Plan data
    const mobilePlans = ref([
      {
        name: 'Mobile GO',
        type: 'mobile',
        dataAllowance: '5GB',
        callingAllowance: '200',
        smsAllowance: '200',
        price12: 24,
        price24: 22,
        description: 'Essential mobile plan with 5GB data for light users. Includes basic EU roaming.',
        features: ['5GB data', '200 minutes', '200 SMS', 'Basic EU roaming']
      },
      {
        name: 'Mobile Flex',
        type: 'mobile',
        dataAllowance: '10GB',
        callingAllowance: '500',
        smsAllowance: '500',
        price12: 32,
        price24: 30,
        description: 'Flexible plan with 10GB data for everyday users. Includes standard EU roaming.',
        features: ['10GB data', '500 minutes', '500 SMS', 'Standard EU roaming']
      },
      {
        name: 'Mobile Ultimate',
        type: 'mobile',
        dataAllowance: '50GB',
        callingAllowance: 'Unlimited',
        smsAllowance: 'Unlimited',
        price12: 50,
        price24: 46,
        description: 'Premium plan with 50GB data for heavy users. Includes premium worldwide roaming.',
        features: ['50GB data', 'Unlimited calls', 'Unlimited SMS', 'Premium worldwide roaming']
      }
    ])

    const tvPlans = ref([
      {
        name: 'TV Basic',
        type: 'tv',
        price12: 18,
        price24: 17,
        description: 'Basic TV package with 60+ channels including news, entertainment and documentaries. Enjoy HD quality on selected channels.',
        channels: 60,
        features: ['60+ channels', 'HD quality', '1 device streaming', 'Catch-up TV (48h)'],
        installFee: 55
      },
      {
        name: 'TV Premium',
        type: 'tv',
        price12: 27,
        price24: 25,
        description: 'Premium TV package with 120+ channels including exclusive sports events, latest movies, and premium entertainment. Multi-device streaming included.',
        channels: 120,
        features: ['120+ channels', '4K quality', '3 device streaming', '7-day replay', 'Premium sports'],
        installFee: 55
      }
    ])

    const fixedPlans = ref([
      {
        name: 'Fixed Telephony',
        type: 'fixed',
        callingAllowance: '200',
        price12: 12,
        price24: 11,
        description: 'Home phone service with 200 minutes to landlines and mobiles.'
      }
    ])

    const internetPlans = ref([
      {
        name: 'Fiber Connect 100MBit',
        type: 'internet',
        price12: 45,
        price24: 40,
        speed: '100Mbit',
        description: 'Fast fiber connection with 100Mbit download speeds. Perfect for everyday streaming and browsing.',
        features: ['100Mbit download', '20Mbit upload', 'Unlimited data', 'Wi-Fi router included'],
        installFee: 85
      },
      {
        name: 'Fiber Ultimate 1Gbit',
        type: 'internet',
        price12: 55,
        price24: 50,
        speed: '1Gbit',
        description: 'Ultra-fast fiber connection with 1Gbit download speeds. Ideal for 4K streaming and gaming.',
        features: ['1Gbit download', '100Mbit upload', 'Unlimited data', 'Premium Wi-Fi router included'],
        installFee: 85
      }
    ])

    // Function to select a plan
    const selectPlan = (type, plan) => {
      selectedPlans[type] = plan;
      saveSelections();
    }
    
    // Get plans for a specific category
    const getCategoryPlans = (category) => {
      switch(category) {
        case 'mobile': return mobilePlans.value
        case 'tv': return tvPlans.value
        case 'internet': return internetPlans.value
        case 'fixed': return fixedPlans.value
        default: return []
      }
    }
    
    // Get category label
    const getCategoryLabel = (type) => {
      const category = categories.find(cat => cat.type === type)
      return category ? category.label : type
    }
    
    // Get category icon class
    const getCategoryIcon = (type) => {
      const category = categories.find(cat => cat.type === type)
      return category ? 'bi ' + category.icon : 'bi bi-question-circle'
    }
    
    // Toggle a category on/off
    const toggleCategory = (type, event) => {
      if (event.target.checked) {
        // Enable category with the first plan
        const plans = getCategoryPlans(type)
        if (plans.length > 0) {
          selectedPlans[type] = plans[0]
        }
      } else {
        // Disable category
        selectedPlans[type] = null
      }
      
      // Save to localStorage
      saveSelections();
    }
    
    // Enable a category
    const enableCategory = (type) => {
      const plans = getCategoryPlans(type)
      if (plans.length > 0) {
        selectedPlans[type] = plans[0]
        
        // Save to localStorage
        saveSelections();
      }
    }
    
    // Get plan description
    const getPlanDescription = (plan) => {
      return plan.description || 'No description available.'
    }

    // Get only selected plans for summary (no nulls)
    const getSelectedPlans = () => {
      const result = {}
      Object.keys(selectedPlans).forEach(key => {
        result[key] = selectedPlans[key]  // Include null values for display
      })
      return result
    }

    // Calculate subtotal before discount
    const calculateSubtotal = () => {
      let total = 0
      Object.values(selectedPlans).forEach(plan => {
        if (plan) {
          total += isLongContract.value ? plan.price24 : plan.price12
        }
      })
      return total.toFixed(2)
    }

    // Check if eligible for bundle discount (at least 3 services)
    const isEligibleForDiscount = () => {
      const count = Object.values(selectedPlans).filter(plan => plan !== null).length
      return count >= 3
    }

    // Calculate discount (10% if eligible)
    const calculateDiscount = () => {
      if (isEligibleForDiscount()) {
        return (parseFloat(calculateSubtotal()) * 0.1).toFixed(2)
      }
      return 0
    }

    // Calculate total after discount
    const calculateTotal = () => {
      const subtotal = parseFloat(calculateSubtotal())
      const discount = isEligibleForDiscount() ? subtotal * 0.1 : 0
      return (subtotal - discount).toFixed(2)
    }
    
    // Calculate installation fees
    const calculateInstallFees = () => {
      let totalFee = 0;
      
      // TV installation fee
      if (selectedPlans.tv) {
        totalFee += isLongContract.value ? 0 : selectedPlans.tv.installFee;
      }
      
      // Internet installation fee
      if (selectedPlans.internet) {
        totalFee += isLongContract.value ? 0 : selectedPlans.internet.installFee;
      }
      
      return totalFee.toFixed(2);
    }
    
    // Mock order submission
    const showOrderSummary = ref(false);
    const submitOrder = () => {
      showOrderSummary.value = true;
    }
    
    // Close order summary
    const closeOrderSummary = () => {
      showOrderSummary.value = false;
    }
    
    // Clear all saved data (for testing)
    const clearSavedData = () => {
      try {
        localStorage.removeItem('planSelector.selectedPlans');
        localStorage.removeItem('planSelector.isLongContract');
        localStorage.removeItem('planSelector.activeCategory');
        console.log('All saved plan selection data cleared');
      } catch (e) {
        console.error('Error clearing localStorage:', e);
      }
    }
    
    // Reset plans to defaults
    const resetPlans = () => {
      // Reset to default plans
      selectedPlans.mobile = plansByName.mobile['Mobile Flex'];
      selectedPlans.tv = plansByName.tv['TV Premium'];
      selectedPlans.internet = plansByName.internet['Fiber Connect 100MBit'];
      selectedPlans.fixed = null;
      
      // Reset contract length
      isLongContract.value = true;
      
      // Reset active category
      activeCategory.value = 'mobile';
      
      // Save changes
      saveSelections();
      storeValue('isLongContract', true);
      storeValue('activeCategory', 'mobile');
    }
    
    // Initialize plan lookup maps for storage/retrieval
    const initPlanMaps = () => {
      // Map mobile plans by name
      mobilePlans.value.forEach(plan => {
        plansByName.mobile[plan.name] = plan;
      });
      
      // Map TV plans by name
      tvPlans.value.forEach(plan => {
        plansByName.tv[plan.name] = plan;
      });
      
      // Map internet plans by name
      internetPlans.value.forEach(plan => {
        plansByName.internet[plan.name] = plan;
      });
      
      // Map fixed plans by name
      fixedPlans.value.forEach(plan => {
        plansByName.fixed[plan.name] = plan;
      });
    };
    
    // Save selected plans to localStorage
    const saveSelections = () => {
      const serializedSelections = {
        mobile: selectedPlans.mobile ? selectedPlans.mobile.name : null,
        tv: selectedPlans.tv ? selectedPlans.tv.name : null,
        internet: selectedPlans.internet ? selectedPlans.internet.name : null,
        fixed: selectedPlans.fixed ? selectedPlans.fixed.name : null
      };
      
      storeValue('selectedPlans', serializedSelections);
    };
    
    // Load selections from previous session or set defaults
    onMounted(() => {
      // Initialize plan maps first
      initPlanMaps();
      
      // Set plans based on stored selections or defaults
      Object.keys(selectedPlans).forEach(category => {
        if (storedSelections[category] && plansByName[category][storedSelections[category]]) {
          selectedPlans[category] = plansByName[category][storedSelections[category]];
        } else if (category === 'mobile' && plansByName.mobile['Mobile Flex']) {
          selectedPlans.mobile = plansByName.mobile['Mobile Flex'];
        } else if (category === 'tv' && plansByName.tv['TV Premium']) {
          selectedPlans.tv = plansByName.tv['TV Premium'];
        } else if (category === 'internet' && plansByName.internet['Fiber Connect 100MBit']) {
          selectedPlans.internet = plansByName.internet['Fiber Connect 100MBit'];
        }
      });
    });
    
    // Set up watchers to save state changes
    
    // Watch for changes to selected plans
    watch(selectedPlans, () => {
      saveSelections();
    }, { deep: true });
    
    // Watch for contract length changes
    watch(isLongContract, (newValue) => {
      storeValue('isLongContract', newValue);
    });
    
    // Watch for active category changes
    watch(activeCategory, (newValue) => {
      storeValue('activeCategory', newValue);
    });

    return {
      isLongContract,
      activeCategory,
      categories,
      selectedPlans,
      mobilePlans,
      tvPlans,
      fixedPlans,
      internetPlans,
      selectPlan,
      getCategoryPlans,
      getCategoryLabel,
      getCategoryIcon,
      toggleCategory,
      enableCategory,
      getPlanDescription,
      getSelectedPlans,
      calculateSubtotal,
      isEligibleForDiscount,
      calculateDiscount,
      calculateTotal,
      calculateInstallFees,
      showOrderSummary,
      submitOrder,
      closeOrderSummary,
      clearSavedData,
      resetPlans
    }
  }
}
</script>

<style scoped>
.quadstar-plan-selector {
  font-family: var(--font-family, 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif);
}

.selection-header {
  margin-bottom: 0.75rem;
}

.saved-notice {
  font-size: 0.7rem;
  color: var(--success-color, #198754);
  opacity: 0.7;
}

.contract-toggle-btn {
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  border-radius: 20px;
  overflow: hidden;
}

.contract-toggle-btn .btn {
  border-radius: 0;
  padding: 0.4rem 0.8rem;
  font-size: 0.85rem;
  border: none;
  transition: all 0.2s ease;
}

.badge {
  font-size: 0.7rem;
  padding: 0.35em 0.65em;
  border-radius: 20px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

/* New layout with tabs and panel */
.categories-and-summary {
  display: grid;
  grid-template-columns: 15rem 1fr 18rem;
  gap: 0.75rem;
  min-height: 25rem;
}

/* Category tabs on the left */
.categories-tabs {
  background-color: var(--bg-secondary, #f8f9fa);
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  border: 1px solid var(--border-color, rgba(0, 0, 0, 0.05));
}

.category-tab {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.9rem 1rem;
  cursor: pointer;
  border-bottom: 1px solid var(--border-color, rgba(0, 0, 0, 0.05));
  transition: all 0.2s ease;
  position: relative;
}

.category-tab:last-child {
  border-bottom: none;
}

.category-tab.active {
  background-color: var(--selection-bg, rgba(13, 110, 253, 0.05));
  border-left: 4px solid var(--primary-color, #0d6efd);
  padding-left: calc(1rem - 4px);
}

.category-tab:hover {
  background-color: var(--hover-bg, rgba(0, 0, 0, 0.02));
}

.category-icon {
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, var(--primary-color, #0d6efd), var(--primary-color-light, #0d8ffd));
  color: white;
  font-size: 1rem;
  margin-right: 0.75rem;
  flex-shrink: 0;
}

.category-name {
  flex-grow: 1;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: var(--text-primary, #212529);
}

.category-selected {
  color: var(--primary-color, #0d6efd);
  margin-left: 0.5rem;
}

.category-toggle {
  margin-left: 0.5rem;
}

/* Plans display in the middle */
.plans-display {
  background-color: var(--bg-secondary, #f8f9fa);
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid var(--border-color, rgba(0, 0, 0, 0.05));
  overflow: auto;
  padding: 1rem;
}

.no-plan-selected {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.no-plan-icon {
  font-size: 3rem;
  color: var(--text-secondary, #6c757d);
  margin-bottom: 1rem;
}

.plan-cards-container {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.plan-card {
  background-color: var(--card-bg, white);
  border-radius: 8px;
  padding: 0.75rem;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid var(--border-color, rgba(0, 0, 0, 0.05));
  display: flex;
  position: relative;
  overflow: hidden;
}

.plan-card:hover {
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.08);
}

.plan-card.selected {
  border-color: var(--primary-color, #0d6efd);
  background-color: var(--selection-bg, rgba(13, 110, 253, 0.05));
}

.plan-card.selected::after {
  content: '\F26E';
  font-family: "bootstrap-icons";
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  color: var(--primary-color, #0d6efd);
  font-size: 1.25rem;
}

.plan-visual {
  width: 3.5rem;
  height: 3.5rem;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
}

.plan-icon {
  font-size: 2rem;
  color: var(--primary-color, #0d6efd);
}

.plan-details {
  flex-grow: 1;
}

.plan-card-header {
  font-weight: 600;
  font-size: 1rem;
  margin-bottom: 0.2rem;
  color: var(--text-primary, #212529);
}

.plan-card-price {
  font-weight: 700;
  font-size: 1.5rem;
  margin-bottom: 0.25rem;
  color: var(--primary-color, #0d6efd);
}

.plan-card-description {
  font-size: 0.85rem;
  color: var(--text-secondary, #6c757d);
  margin-bottom: 0.5rem;
}

.plan-card-features {
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
}

.feature-pill {
  display: inline-block;
  background-color: var(--bg-secondary, rgba(13, 110, 253, 0.1));
  color: var(--primary-color, #0d6efd);
  padding: 0.2rem 0.5rem;
  border-radius: 12px;
  font-size: 0.7rem;
  white-space: nowrap;
}

.installation-notice {
  margin-top: 1rem;
  font-size: 0.75rem;
  color: var(--text-secondary, #6c757d);
  background-color: rgba(0, 0, 0, 0.03);
  padding: 0.5rem;
  border-radius: 6px;
  display: flex;
  align-items: center;
}

.installation-notice i {
  font-size: 1rem;
  margin-right: 0.5rem;
  color: var(--warning-color, #ffc107);
}

.overage-rates {
  margin-top: 1rem;
  font-size: 0.7rem;
  color: var(--text-secondary, #6c757d);
  background-color: rgba(0, 0, 0, 0.03);
  padding: 0.5rem;
  border-radius: 6px;
}

.overage-title {
  font-weight: 600;
  margin-bottom: 0.25rem;
}

.overage-detail {
  font-size: 0.7rem;
  margin-bottom: 0.1rem;
}

/* Summary panel on the right */
.summary-panel {
  background-color: var(--bg-secondary, #f8f9fa);
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid var(--border-color, rgba(0, 0, 0, 0.05));
  padding: 1rem;
}

.summary-panel h3 {
  font-size: 1rem;
  margin-bottom: 0.75rem;
  padding-bottom: 0.4rem;
  border-bottom: 2px solid var(--primary-color, #0d6efd);
  display: inline-block;
}

.summary-items {
  margin-bottom: 0.75rem;
}

.summary-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.4rem;
  padding-bottom: 0.4rem;
  border-bottom: 1px solid var(--border-color, rgba(0, 0, 0, 0.05));
}

.summary-icon {
  font-size: 0.9rem;
  margin-right: 0.5rem;
  color: var(--primary-color, #0d6efd);
}

.summary-label {
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--text-primary, #212529);
}

.summary-price {
  font-weight: 600;
  font-size: 0.9rem;
  color: var(--text-primary, #212529);
}

.summary-total {
  display: flex;
  justify-content: space-between;
  padding-top: 0.4rem;
  font-weight: 600;
  color: var(--text-primary, #212529);
}

.discount-row {
  display: flex;
  justify-content: space-between;
  padding: 0.4rem 0;
  border-bottom: 1px solid var(--border-color, rgba(0, 0, 0, 0.05));
  color: var(--success-color, #198754);
}

.install-fee-row {
  display: flex;
  justify-content: space-between;
  padding: 0.4rem 0;
  border-bottom: 1px solid var(--border-color, rgba(0, 0, 0, 0.05));
  color: var(--warning-color, #fd7e14);
}

.fee-label, .fee-value {
  font-weight: 600;
}

.contract-note {
  font-size: 0.75rem;
  color: var(--warning-color, #fd7e14);
  margin: 0.5rem 0;
  display: flex;
  align-items: center;
}

.contract-note i {
  margin-right: 0.35rem;
}

.grand-total {
  margin-top: 0.4rem;
  font-size: 1.15rem;
  color: var(--primary-color, #0d6efd);
}

/* Order summary modal */
.order-summary-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.order-summary-content {
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  background-color: var(--bg-color, white);
  border-radius: 8px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
  overflow: auto;
}

.order-summary-header {
  padding: 1rem;
  border-bottom: 1px solid var(--border-color, rgba(0, 0, 0, 0.1));
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  background-color: var(--bg-color, white);
  z-index: 10;
}

.order-summary-header h2 {
  margin: 0;
  font-size: 1.5rem;
  color: var(--text-primary, #212529);
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: var(--text-secondary, #6c757d);
}

.order-summary-body {
  padding: 1rem;
}

.order-section {
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--border-color, rgba(0, 0, 0, 0.1));
}

.order-section:last-child {
  border-bottom: none;
  margin-bottom: 0.5rem;
}

.order-section h3 {
  font-size: 1.1rem;
  margin-bottom: 1rem;
  color: var(--text-primary, #212529);
}

.order-plan-item {
  margin-bottom: 1rem;
}

.plan-item-details {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem;
  background-color: var(--bg-secondary, #f8f9fa);
  border-radius: 8px;
}

.plan-item-icon {
  width: 2.5rem;
  height: 2.5rem;
  background: linear-gradient(135deg, var(--primary-color, #0d6efd), var(--primary-color-light, #0d8ffd));
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.2rem;
}

.plan-item-info {
  flex-grow: 1;
}

.plan-item-name {
  font-weight: 600;
  color: var(--text-primary, #212529);
}

.plan-item-desc {
  font-size: 0.8rem;
  color: var(--text-secondary, #6c757d);
}

.plan-item-price {
  font-weight: 700;
  color: var(--primary-color, #0d6efd);
}

.order-detail-item {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
  color: var(--text-primary, #212529);
}

.order-detail-item.discount {
  color: var(--success-color, #198754);
}

.order-detail-item.fee {
  color: var(--warning-color, #fd7e14);
}

.order-detail-item.total {
  font-weight: 700;
  margin-top: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px solid var(--border-color, rgba(0, 0, 0, 0.1));
}

.order-actions {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
}

/* Dark mode adjustments for order summary */
[data-theme="dark"] .order-summary-content,
[data-theme="dark"] .order-summary-header {
  background-color: var(--bg-secondary, #272a30);
  color: var(--text-light, #f8f9fa);
}

[data-theme="dark"] .order-summary-header h2,
[data-theme="dark"] .order-section h3,
[data-theme="dark"] .plan-item-name,
[data-theme="dark"] .order-detail-item {
  color: var(--text-light, #f8f9fa);
}

[data-theme="dark"] .plan-item-details {
  background-color: rgba(255, 255, 255, 0.05);
}

.subscribe-btn {
  border-radius: 6px;
  padding: 0.5rem;
  font-weight: 500;
  transition: all 0.2s ease;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.15);
}

.subscribe-btn:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.reset-btn {
  font-size: 0.75rem;
  text-decoration: none;
  padding: 0.25rem 0.5rem;
  color: var(--text-muted, #6c757d);
}

.reset-btn:hover {
  text-decoration: underline;
  color: var(--text-muted, #6c757d);
}

/* Dark mode specific adjustments */
[data-theme="dark"] .categories-tabs,
[data-theme="dark"] .plans-display,
[data-theme="dark"] .summary-panel {
  background-color: var(--bg-secondary, #272a30);
  border-color: var(--border-color, rgba(255, 255, 255, 0.05));
}

[data-theme="dark"] .category-tab:hover {
  background-color: var(--hover-bg, rgba(255, 255, 255, 0.05));
}

[data-theme="dark"] .category-tab.active {
  background-color: var(--selection-bg, rgba(13, 110, 253, 0.15));
}

[data-theme="dark"] .plan-card {
  background-color: var(--card-bg, #2a2d33);
  border-color: var(--border-color, rgba(255, 255, 255, 0.05));
}

[data-theme="dark"] .plan-card.selected {
  background-color: var(--selection-bg, rgba(13, 110, 253, 0.15));
}

[data-theme="dark"] .category-name,
[data-theme="dark"] .plan-card-header,
[data-theme="dark"] .summary-label,
[data-theme="dark"] .summary-price,
[data-theme="dark"] .total-label,
[data-theme="dark"] .total-value {
  color: var(--text-light, #f8f9fa);
}

/* Responsive styling */
@media (max-width: 1200px) {
  .categories-and-summary {
    grid-template-columns: 14rem 1fr 16rem;
  }
}

@media (max-width: 992px) {
  .categories-and-summary {
    grid-template-columns: 12rem 1fr;
    grid-template-rows: auto auto;
  }
  
  .summary-panel {
    grid-column: 1 / 3;
    grid-row: 2;
    margin-top: 0.75rem;
  }
}

@media (max-width: 768px) {
  .categories-and-summary {
    grid-template-columns: 1fr;
    grid-template-rows: auto auto auto;
  }
  
  .categories-tabs {
    display: flex;
    flex-wrap: wrap;
    overflow-x: auto;
    padding: 0.5rem;
  }
  
  .category-tab {
    width: auto;
    border-bottom: none;
    border-radius: 8px;
    padding: 0.5rem 0.75rem;
    margin-right: 0.5rem;
    margin-bottom: 0.5rem;
  }
  
  .category-tab.active {
    border-left: none;
    padding-left: 0.75rem;
  }
  
  .category-name {
    font-size: 0.85rem;
  }
  
  .category-icon {
    width: 1.5rem;
    height: 1.5rem;
    font-size: 0.85rem;
    margin-right: 0.5rem;
  }
  
  .category-toggle {
    display: none;
  }
  
  .plans-display {
    grid-column: 1;
    grid-row: 2;
  }
  
  .summary-panel {
    grid-column: 1;
    grid-row: 3;
  }
  
  .plan-card {
    flex-direction: row;
  }
  
  .plan-visual {
    width: 2.5rem;
    height: 2.5rem;
    margin-right: 0.75rem;
  }
  
  .plan-icon {
    font-size: 1.5rem;
  }
}
</style>
